// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { Flex, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useConsentMutation } from '../../api/hooks/consentMutation';
import { useIdentifyPatientMutation } from '../../api/hooks/identifyPatientMutation';
import { usePostGrantMutation } from '../../api/hooks/postGrantMutation';
import faceAlignmentGuide from '../../assets/svg/faceAlignmentGuide.svg';
import CameraCard from '../../components/camera/CameraCard';
import { ErrorComponent } from '../../components/error/ErrorComponent';
import { Button, HexagonSpinner } from '@imprivata-cloud/patient-access-ui-components';
import { usePlatform } from '../../context/platform/hooks';
import { generateBaseAttributes, tracingService } from '../../tracing';
import { useFaceCapture } from '../verify/hooks/useFaceCapture';
import './IdentifyPage.less';

const { Title, Text } = Typography;

type IdentifyState =
  | 'INIT'
  | 'CAPTURING'
  | 'SUBMITTING'
  | 'SUCCESS'
  | 'DENIED'
  | 'ERROR'
  | 'TIMEOUT'
  | 'CANCELLED';

const IDENTIFICATION_TIMEOUT_MS = 30000;

const IdentifyPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { featureFlags } = usePlatform();
  const [state, setState] = useState<IdentifyState>('INIT');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasSubmittedRef = useRef(false);

  const { enableDebugLogging, tenantId, errorCode } = useMemo(() => {
    const debugEnabled = searchParams.get('debug') === 'true';
    const tenant = searchParams.get('Impr-Tenant-Id');
    const errCode = searchParams.get('error-code');
    return { enableDebugLogging: debugEnabled, tenantId: tenant, errorCode: errCode };
  }, [searchParams]);

  const isFeatureEnabled = featureFlags.OIDC_PATIENT_IDENTIFICATION_ENABLED;

  // Consent mutation — identification flow has no user-facing consent step,
  // so we auto-grant consent before calling postGrant on success.
  const { mutate: consentMutate } = useConsentMutation();

  // Grant/deny redirect hook — backend uses the same /grant endpoint for both;
  // outcome depends on whether a patient session was established by /identify
  const { postGrant } = usePostGrantMutation({});

  // Handle successful identification
  const handleIdentifySuccess = useCallback(
    (patientId: string) => {
      if (patientId) {
        setState('SUCCESS');
        tracingService.startSpan('patient_identification_result', {
          ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
          page: 'identify',
          result: 'match',
        });
        tracingService.endSpan();

        tracingService.startSpan('patient_identification_redirect', {
          ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
          page: 'identify',
          redirect_type: 'grant',
        });
        tracingService.endSpan();

        // Auto-grant consent then redirect — no patient-facing consent step in POC identification
        consentMutate(true, {
          onSuccess: () => {
            postGrant();
          },
        });
      } else {
        setState('DENIED');
        tracingService.startSpan('patient_identification_result', {
          ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
          page: 'identify',
          result: 'no_match',
        });
        tracingService.endSpan();
        postGrant();
      }
    },
    [tenantId, postGrant, consentMutate],
  );

  // Identify mutation
  const { identifyPatient, apiError, isNetworkError, isPending } = useIdentifyPatientMutation({
    onSuccess: handleIdentifySuccess,
  });

  // Handle capture complete — submit photo for identification
  const handleCaptureComplete = useCallback(
    (photoBase64: string) => {
      if (hasSubmittedRef.current) {
        return;
      }
      hasSubmittedRef.current = true;
      setState('SUBMITTING');

      tracingService.startSpan('patient_identification_capture', {
        ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
        page: 'identify',
        action: 'auto_capture',
      });
      tracingService.endSpan();

      tracingService.startSpan('patient_identification_search', {
        ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
        page: 'identify',
        action: 'search_submit',
      });
      tracingService.endSpan();

      identifyPatient(photoBase64);
    },
    [tenantId, identifyPatient],
  );

  // Face capture hook with rear-facing camera preference
  const {
    videoRef,
    canvasRef,
    captureState,
    isInitializing,
    handleCapture,
    onVideoElementReady,
    onVideoPlay,
  } = useFaceCapture({
    enableParavisionSdk: true,
    enableDebugLogging,
    onCaptureComplete: handleCaptureComplete,
    preferredFacingMode: 'environment',
  });

  // Auto-capture: trigger handleCapture once camera is ready and state is CAPTURING.
  // Uses a useEffect instead of onVideoPlay callback to avoid stale closure
  // in CameraCard's memoized videoElement (whose onPlay captures initial props).
  const hasStartedCaptureRef = useRef(false);

  useEffect(() => {
    if (
      !isInitializing &&
      state === 'CAPTURING' &&
      !hasStartedCaptureRef.current &&
      !captureState.feedback.cameraError
    ) {
      hasStartedCaptureRef.current = true;
      handleCapture().catch((error: unknown) => {
        tracingService.startSpan('patient_identification_capture_error', {
          ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
          page: 'identify',
          action: 'capture_rejected',
          error_message: error instanceof Error ? error.message : 'unknown',
        });
        tracingService.endSpan();
      });
    }
  }, [isInitializing, state, captureState.feedback.cameraError, handleCapture, tenantId]);

  // Transition from INIT to CAPTURING if feature is enabled and no error-code
  useEffect(() => {
    if (state === 'INIT' && isFeatureEnabled && !errorCode) {
      setState('CAPTURING');

      tracingService.startSpan('patient_identification_launch', {
        ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
        page: 'identify',
      });
      tracingService.endSpan();
    }
  }, [state, isFeatureEnabled, errorCode, tenantId]);

  // Timeout: start once on entering CAPTURING, don't reset on CAPTURING→SUBMITTING
  const timeoutStartedRef = useRef(false);

  useEffect(() => {
    if (state === 'CAPTURING' && !timeoutStartedRef.current) {
      timeoutStartedRef.current = true;
      timeoutRef.current = setTimeout(() => {
        setState((current) => {
          if (current === 'CAPTURING' || current === 'SUBMITTING') {
            tracingService.startSpan('patient_identification_result', {
              ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
              page: 'identify',
              result: 'timeout',
            });
            tracingService.endSpan();
            postGrant();
            return 'TIMEOUT';
          }
          return current;
        });
      }, IDENTIFICATION_TIMEOUT_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [state, tenantId, postGrant]);

  // Handle API errors — distinguish reachable backend (deny redirect) from unreachable (ErrorComponent)
  useEffect(() => {
    if (apiError) {
      setState('ERROR');
      tracingService.startSpan('patient_identification_result', {
        ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
        page: 'identify',
        result: 'error',
        error_type: isNetworkError ? 'network' : 'api',
      });
      tracingService.endSpan();

      if (!isNetworkError) {
        // Backend responded with an error — redirect via deny flow
        tracingService.startSpan('patient_identification_redirect', {
          ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
          page: 'identify',
          redirect_type: 'deny',
        });
        tracingService.endSpan();
        postGrant();
      }
      // Network error: no redirect — ErrorComponent rendered below
    }
  }, [apiError, isNetworkError, tenantId, postGrant]);

  // Handle cancellation (cancel button or browser back)
  const handleCancel = useCallback(() => {
    setState((current) => {
      if (current === 'CAPTURING' || current === 'SUBMITTING') {
        tracingService.startSpan('patient_identification_result', {
          ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
          page: 'identify',
          result: 'cancelled',
        });
        tracingService.endSpan();
        postGrant();
        return 'CANCELLED';
      }
      return current;
    });
  }, [tenantId, postGrant]);

  // Browser back button handler
  useEffect(() => {
    if (state !== 'CAPTURING' && state !== 'SUBMITTING') {
      return undefined;
    }

    // Push a history entry so back button triggers popstate instead of navigating away
    window.history.pushState({ identify: true }, '');

    const onPopState = () => {
      handleCancel();
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [state, handleCancel]);

  // Handle camera error
  useEffect(() => {
    if (captureState.feedback.cameraError && state === 'CAPTURING') {
      setState('ERROR');
      tracingService.startSpan('patient_identification_result', {
        ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
        page: 'identify',
        result: 'error',
      });
      tracingService.endSpan();
      postGrant();
    }
  }, [captureState.feedback.cameraError, state, tenantId, postGrant]);

  // Feature flag disabled
  if (!isFeatureEnabled) {
    return <ErrorComponent cause="feature-disabled" />;
  }

  // Error code from backend redirect
  if (errorCode) {
    return <ErrorComponent cause={errorCode} />;
  }

  // Submitting / pending
  if (state === 'SUBMITTING' || isPending) {
    return (
      <Flex vertical align="center" className="identify-page" data-testid="identify-page">
        <HexagonSpinner data-testid="loading-spinner" />
        <Text data-testid="identify-submitting">{t('identifyPage.identifying-patient')}</Text>
      </Flex>
    );
  }

  // Success (brief — postGrant redirects browser away)
  if (state === 'SUCCESS') {
    return (
      <Flex vertical align="center" className="identify-page" data-testid="identify-page">
        <Title level={2} data-testid="identify-success">
          {t('identifyPage.identification-successful')}
        </Title>
      </Flex>
    );
  }

  // Timeout
  if (state === 'TIMEOUT') {
    return (
      <Flex vertical align="center" className="identify-page" data-testid="identify-page">
        <Text data-testid="identify-timeout">{t('identifyPage.identification-timeout')}</Text>
      </Flex>
    );
  }

  // Network error — backend unreachable, show ErrorComponent (REQ-014)
  if (state === 'ERROR' && isNetworkError) {
    return <ErrorComponent cause="network-error" />;
  }

  // Error / Denied / Cancelled — postGrant redirects browser away (no patient session → access_denied)
  if (state === 'ERROR' || state === 'DENIED' || state === 'CANCELLED') {
    return (
      <Flex vertical align="center" className="identify-page" data-testid="identify-page">
        <HexagonSpinner data-testid="loading-spinner" />
      </Flex>
    );
  }

  // Guidance text from the face capture service, or default instruction
  const guidanceText =
    captureState.feedback.title && !captureState.feedback.cameraError
      ? captureState.feedback.title
      : t('identifyPage.center-face-instruction');

  // Show face guide when video is playing and not capturing/captured
  const showFaceGuide =
    !isInitializing &&
    !captureState.capturing &&
    !captureState.captured &&
    !captureState.feedback.cameraError;

  // INIT / CAPTURING — show camera
  return (
    <Flex vertical align="center" className="identify-page" data-testid="identify-page">
      <Title level={2} data-testid="identify-title">
        {t('identifyPage.look-at-camera')}
      </Title>
      <Text className="identify-instructions" data-testid="identify-instructions">
        {guidanceText}
      </Text>
      <div className="identification-content">
        <CameraCard
          capturing={captureState.capturing}
          captured={captureState.captured}
          video={videoRef}
          canvas={canvasRef}
          cameraError={captureState.feedback.cameraError}
          isInitializing={isInitializing}
          onVideoPlay={onVideoPlay}
          onVideoElementReady={onVideoElementReady}
        />
        {showFaceGuide && (
          <img
            src={faceAlignmentGuide}
            alt=""
            className="face-alignment-guide"
            data-testid="face-alignment-guide"
          />
        )}
      </div>
      <Button
        variant="secondary"
        label={t('identifyPage.cancel')}
        onClick={handleCancel}
        className="mt-1"
        data-testid="identify-cancel-button"
      />
    </Flex>
  );
};

export default IdentifyPage;
